import { useEffect } from 'react';

/**
 * Which dialogs currently own the keyboard.
 *
 * A modal traps Tab inside itself, and that is the whole of what a focus trap
 * can do. It cannot stop a listener bound to `window` — the shortcut handler in
 * `app-layout.tsx` is one — because the keystroke reaches that listener by
 * bubbling through the DOM, not through the dialog's React tree. So a dialog
 * that has covered the page and taken focus is still, from a global shortcut's
 * point of view, invisible: ⌘K over an open confirmation opened the command
 * palette as a SECOND `aria-modal` layer at the same stacking level, with focus
 * management of its own and an unanswered question live behind it.
 *
 * Guarding one shortcut against one dialog's own state is what the sign-out
 * confirmation did first, and it does not scale past the dialog it was written
 * for: there are a dozen `ConfirmDialog` call sites and the palette knows about
 * none of them. This module is the general form — dialogs declare that they
 * hold the keyboard, global handlers ask whether anybody does — so a new
 * confirmation added anywhere is covered on the day it is written rather than
 * the day somebody notices.
 *
 * ## What does NOT register
 *
 * Holding the keyboard is a claim about a dialog the reader must answer or
 * dismiss, not about anything that happens to be `aria-modal`:
 *
 *   * The **command palette** is the shortcut's own surface. Registering it
 *     would make ⌘K unable to close the thing ⌘K opened.
 *   * The phone navigation's **More sheet** is chrome, and the shortcut
 *     deliberately sweeps it aside to make room (see the handler). Nothing is
 *     lost by closing a list of links; a half-answered question is different
 *     in kind.
 *
 * ## Why a count
 *
 * A confirmation can be raised from inside another one, and the inner dialog
 * unmounting must not hand the keyboard back while the outer is still up. The
 * count also makes the register/release pairing self-checking: it is symmetric
 * by construction, because the release is the effect's own cleanup.
 */
let held = 0;

/**
 * Declares that this dialog holds the keyboard for as long as it is mounted.
 *
 * Called by the dialog itself rather than by each of its callers — a modal is
 * modal wherever it is opened from, and a rule every call site has to remember
 * is a rule that will be missed at the thirteenth one.
 */
export function useKeyboardHold(): void {
  useEffect(holdKeyboardForModal, []);
}

/** The same hold for imperative browser dialogs; release is idempotent. */
export function holdKeyboardForModal(): () => void {
  held += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    held = Math.max(0, held - 1);
  };
}

/**
 * Whether any mounted dialog is holding the keyboard.
 *
 * Read at event time from a global handler rather than subscribed to. A
 * shortcut only needs the answer in the instant the key is pressed, and
 * subscribing would re-render the whole app shell every time any confirmation
 * anywhere opened or closed.
 */
export function keyboardHeldByModal(): boolean {
  return held > 0;
}

/**
 * Test-only reset, for a suite whose dialog was torn down without unmounting.
 * Nothing in the app calls this: the count is maintained by effect cleanup.
 *
 * Safe to call with dialogs still mounted only because the release above is
 * clamped — their cleanups then land on an already-zeroed count instead of
 * pushing it negative and turning the guard off for the rest of the run.
 */
export function resetKeyboardHoldForTests(): void {
  held = 0;
}
